'use client'

import { useState, useEffect, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Search, Building2, ChevronLeft, ChevronRight } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'

import { Account } from '@/core/api/entities/account/account'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { useListAccounts } from '@/core/hooks/account/useAccount'
import { useRefetchAccountData } from '@/core/hooks/account/useRefetchAccountData'
import { usePermission } from '@/core/hooks/usePermission'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import {
	enableFetchAllData,
	disableFetchAllData,
} from '@/core/store/slices/fetchAllDataSlice'
import { clearSelectedAccount } from '@/core/store/slices/selectedAccountSlice'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { RadioGroup } from '@/shared/components/atoms/Radio'
import { RadioGroupItem } from '@/shared/components/atoms/Radio'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import { Switch } from '@/shared/components/atoms/Switch'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10

interface AccountSelectorModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export const AccountSelectorModal = ({
	open,
	onOpenChange,
}: AccountSelectorModalProps) => {
	const { t } = useTranslation('common')
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const { hasAnyPermission } = usePermission()
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
		null
	)
	const [page, setPage] = useState(DEFAULT_PAGE)
	const [isConfirming, setIsConfirming] = useState(false)
	const [isMobile, setIsMobile] = useState(() => {
		if (typeof window === 'undefined') return false
		return window.matchMedia('(max-width: 767px)').matches
	})
	const pageSize = DEFAULT_PAGE_SIZE

	// Use the centralized refetch hook
	const refetchAccountData = useRefetchAccountData()

	// Debounce search query for server-side search
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	const currentSelectedAccount = useSelector(
		(state: RootState) => state.selectedAccount.selectedAccount
	)

	const fetchAllDataEnabled = useSelector(
		(state: RootState) => state.fetchAllData.enabled
	)

	const authenticatedUser = useSelector((state: RootState) => state.auth.user)

	// The current working account — used to check feature eligibility
	const currentAccount = useCurrentAccount()
	const isFetchAllDataEligible =
		currentAccount?.hasFetchAllDataSelectorFeature() ?? false

	// Check if user has required permissions
	const canAccessAccounts = hasAnyPermission([
		PERMISSIONS.LIST_ACCOUNTS,
		PERMISSIONS.MANAGE_CHILDREN_ACCOUNTS,
	])

	// Fetch accounts with pagination and search
	const { data: accountsResponse, isLoading: isLoadingAccounts } =
		useListAccounts({
			options: {
				enabled: open && canAccessAccounts,
			},
			config: {
				params: {
					page,
					limit: pageSize,
					order_by: 'id',
					direction: 'desc',
					query: debouncedSearchQuery || undefined,
					dynamic_filters: JSON.stringify([
						{
							field: 'deleted_at',
							value: '*',
							mode: 'is_null',
						},
						...(authenticatedUser?.account?.id
							? [
									{
										field: 'id',
										value: authenticatedUser.account.id,
										mode: 'not_in',
									},
								]
							: []),
					]),
				},
			},
		})

	const accounts = accountsResponse?.data || []
	const paginationMeta = accountsResponse?.meta?.pagination

	// Reset page to 1 when search query changes
	useEffect(() => {
		setPage(DEFAULT_PAGE)
	}, [debouncedSearchQuery])

	// Initialize selection with current selected account
	useEffect(() => {
		if (open && currentSelectedAccount) {
			setSelectedAccountId(currentSelectedAccount.id)
		} else if (open && !currentSelectedAccount) {
			setSelectedAccountId(null)
		}
	}, [open, currentSelectedAccount])

	// Reset state when modal closes
	useEffect(() => {
		if (!open) {
			setSearchQuery('')
			setPage(DEFAULT_PAGE)
		}
	}, [open])

	useEffect(() => {
		if (typeof window === 'undefined') return

		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handleMediaQueryChange = (event: MediaQueryListEvent) => {
			setIsMobile(event.matches)
		}

		setIsMobile(mediaQuery.matches)
		mediaQuery.addEventListener('change', handleMediaQueryChange)

		return () => {
			mediaQuery.removeEventListener('change', handleMediaQueryChange)
		}
	}, [])

	const handleSelectAccount = (accountId: number) => {
		setSelectedAccountId(accountId)
	}

	// Handle pagination changes
	const onPageChange = useCallback((newPage: number) => {
		setPage(newPage)
	}, [])

	const handleConfirm = async () => {
		if (!selectedAccountId) return

		setIsConfirming(true)
		try {
			// Fetch full account details with all includes using the hook
			await refetchAccountData(selectedAccountId)
			onOpenChange(false)
		} catch (error) {
			console.error('Error fetching account details:', error)
		} finally {
			setIsConfirming(false)
		}
	}

	const handleClearSelection = () => {
		setSelectedAccountId(null)
		dispatch(clearSelectedAccount())
		if (authenticatedUser?.account?.hasFetchAllDataDefaultFeature()) {
			dispatch(enableFetchAllData())
		} else {
			dispatch(disableFetchAllData())
		}
		// Clear React Query cache when clearing selection as well
		queryClient.clear()
		onOpenChange(false)
	}

	const handleCancel = () => {
		// Reset to current selection when canceling
		if (currentSelectedAccount) {
			setSelectedAccountId(currentSelectedAccount.id)
		} else {
			setSelectedAccountId(null)
		}
		onOpenChange(false)
	}

	const handleToggleFetchAllData = (checked: boolean) => {
		if (checked) {
			dispatch(enableFetchAllData())
		} else {
			dispatch(disableFetchAllData())
		}
		queryClient.clear()
	}

	const isLoading = isLoadingAccounts

	const modalBody = (
		<>
			<div className="px-6 py-3 space-y-3">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t('account_selector.search_placeholder')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
					/>
				</div>

				<ScrollArea className="h-[220px] lg:h-[320px] border rounded-md">
					{isLoading ? (
						<div className="flex items-center justify-center h-full text-muted-foreground">
							{t('actions.loading')}
						</div>
					) : accounts.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
							<Building2 className="h-12 w-12 mb-2 opacity-50" />
							<p>{t('account_selector.no_accounts')}</p>
						</div>
					) : (
						<RadioGroup
							value={selectedAccountId?.toString() || ''}
							onValueChange={(value) => handleSelectAccount(Number(value))}
						>
							<div className="divide-y">
								{accounts.map((account: Account) => (
									<div
										key={account.id}
										className="flex items-center space-x-3 p-4 hover:bg-accent cursor-pointer"
										onClick={() => handleSelectAccount(account.id)}
									>
										<RadioGroupItem
											value={account.id.toString()}
											id={`account-${account.id}`}
										/>
										<label
											htmlFor={`account-${account.id}`}
											className="flex-1 cursor-pointer"
										>
											<div className="flex items-center justify-between">
												<div>
													<p className="font-medium flex items-center gap-2">
														{account.name}
														{account.suspended && (
															<Badge variant="destructive">
																{t('common.suspended')}
															</Badge>
														)}
													</p>
													{account.playersCount !== null && (
														<p className="text-sm text-muted-foreground">
															{account.playersCount}{' '}
															{account.playersCount === 1 ? 'zone' : 'zones'}
														</p>
													)}
												</div>
												{account.id && (
													<Badge variant="outline">{account.id}</Badge>
												)}
											</div>
										</label>
									</div>
								))}
							</div>
						</RadioGroup>
					)}
				</ScrollArea>

				{(paginationMeta?.total_pages ?? 1) > 1 && (
					<div className="flex items-center justify-between pt-2">
						<span className="text-xs text-muted-foreground">
							{page} / {paginationMeta?.total_pages ?? 1}
						</span>
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => onPageChange(page - 1)}
								disabled={page === 1}
							>
								<ChevronLeft className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => onPageChange(page + 1)}
								disabled={page >= (paginationMeta?.total_pages ?? 1)}
							>
								<ChevronRight className="h-4 w-4" />
							</Button>
						</div>
					</div>
				)}
			</div>

			{isFetchAllDataEligible && (
				<>
					<Separator />
					<div className="px-6 py-4">
						<Switch
							checked={fetchAllDataEnabled}
							onCheckedChange={handleToggleFetchAllData}
							label={t('account_selector.fetch_all_data_toggle')}
							description={t('account_selector.fetch_all_data_description')}
						/>
					</div>
				</>
			)}

			<Separator />

			<DialogFooter className="px-6 py-4 flex flex-row justify-between">
				{currentSelectedAccount && (
					<Button variant="outline" onClick={handleClearSelection}>
						{t('account_selector.clear_selection')}
					</Button>
				)}
				<div className="flex gap-2">
					<Button variant="outline" onClick={handleCancel}>
						{t('actions.cancel')}
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!selectedAccountId || isConfirming}
					>
						{isConfirming
							? t('actions.loading')
							: t('account_selector.confirm')}
					</Button>
				</div>
			</DialogFooter>
		</>
	)

	return (
		<>
			{isMobile ? (
				<BottomSheet
					open={open}
					onOpenChange={(nextOpen) => {
						if (!nextOpen) {
							handleCancel()
						}
					}}
				>
					<BottomSheetContent className="px-0 pb-6 pt-1" variant="card">
						<div className="px-6 pt-2 pb-1">
							<BottomSheetTitle className="text-xl font-semibold">
								{t('account_selector.title')}
							</BottomSheetTitle>
						</div>
						<Separator />
						{modalBody}
					</BottomSheetContent>
				</BottomSheet>
			) : (
				<Dialog open={open} onOpenChange={handleCancel}>
					<DialogContent className="max-w-3xl p-0 gap-0">
						<DialogHeader className="px-6 pt-5 pb-3">
							<DialogTitle className="text-xl">
								{t('account_selector.title')}
							</DialogTitle>
						</DialogHeader>
						<Separator />
						{modalBody}
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
